import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Report } from '../hooks/useReports';
import { ImageWithLoading } from './ImageWithLoading';
import { GamificationGate } from './GamificationGate';

interface TreasureCardProps {
  report: Report;
  onFavorite?: (reportId: string, isFavorited: boolean) => void;
  isFavorited?: boolean;
  showCoins?: boolean;
  onShare?: (reportId: string) => void;
}

const TreasureCard: React.FC<TreasureCardProps> = ({
  report,
  onFavorite,
  isFavorited = false,
  showCoins = true,
  onShare
}) => {
  const navigate = useNavigate();

  const formatDate = (timestamp: any) => {
    if (!timestamp) return '';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return '';
    }
    
    return date.toLocaleDateString();
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return {
          label: 'Available',
          className: 'bg-verge text-white'
        };
      case 'going_soon':
        return {
          label: 'Going Soon',
          className: 'bg-treasure text-urban'
        };
      case 'gone':
        return {
          label: 'Gone',
          className: 'bg-urban text-white'
        };
      case 'in_review':
        return {
          label: 'In Review',
          className: 'bg-cardboard text-white'
        };
      case 'rejected':
        return {
          label: 'Rejected',
          className: 'bg-rust text-white'
        };
      default:
        return {
          label: status.replace('_', ' ').toUpperCase(),
          className: 'bg-urban-200 text-urban'
        };
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on action buttons
    if ((e.target as HTMLElement).closest('.favorite-button') || 
        (e.target as HTMLElement).closest('button[aria-label="Share this treasure"]')) {
      return;
    }
    navigate(`/reports/${report.id}`);
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFavorite) {
      onFavorite(report.id, !isFavorited);
    }
  };

  const handleShareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onShare) {
      onShare(report.id);
    }
  };

  const statusConfig = getStatusConfig(report.status);

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300 cursor-pointer hover:-translate-y-1 group touch-target"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick(e as any);
        }
      }}
      aria-label={`View details for treasure in ${report.location.suburb}`}
    >
      {/* Image Container with 4:3 Aspect Ratio */}
      <div className="relative">
        <ImageWithLoading
          src={report.images?.[0]?.url || ''}
          alt={report.images?.[0]?.altText || 'Curbside treasure item'}
          aspectRatio="4/3"
          className="group-hover:scale-105 transition-transform duration-300"
          fallbackIcon="📦"
          fallbackText="No image"
        />
        
        {/* Status Badge - positioned over image */}
        <div className="absolute top-3 left-3">
          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${statusConfig.className}`}>
            {statusConfig.label}
          </span>
        </div>
        
        {/* Action Buttons Container */}
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex gap-2">
          {/* Share Button - Mobile Optimized */}
          {onShare && (
            <button
              onClick={handleShareClick}
              className="w-10 h-10 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 touch-target bg-white/90 hover:bg-white text-cardboard-400 hover:text-cardboard"
              aria-label="Share this treasure"
            >
              <svg 
                className="w-4 h-4" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" 
                />
              </svg>
            </button>
          )}

          {/* Favorite/Bookmark Button - heart icon overlay - Mobile Optimized */}
          <button
            onClick={handleFavoriteClick}
            className={`favorite-button w-10 h-10 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110 touch-target ${
              isFavorited 
                ? 'bg-rust text-white shadow-md' 
                : 'bg-white/90 hover:bg-white text-rust-400 hover:text-rust'
            }`}
            aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
          >
            <svg 
              className="w-4 h-4" 
              fill={isFavorited ? 'currentColor' : 'none'} 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
              />
            </svg>
          </button>
        </div>

        {/* Additional image count indicator */}
        {report.images && report.images.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
            </svg>
            {report.images.length}
          </div>
        )}
      </div>

      {/* Card Content - Mobile Optimized */}
      <div className="p-3 sm:p-4">
        {/* Location */}
        <div className="text-xs sm:text-sm text-urban-400 mb-1 sm:mb-2 flex items-center gap-1">
          <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
          </svg>
          <span className="truncate">{report.location.suburb}, {report.location.postcode}</span>
        </div>
        
        {/* Title removed - focusing on visual content and tags */}
        
        {/* Categories and Tags - Mobile Optimized */}
        <div className="mb-2 sm:mb-3 space-y-1 sm:space-y-2">
          {/* Categories - displayed with distinct styling */}
          {report.categories && report.categories.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {report.categories.slice(0, 2).map((category, index) => (
                <span
                  key={`category-${index}`}
                  className="px-2 py-1 text-xs bg-cardboard-100 text-cardboard-800 rounded-lg font-semibold border border-cardboard-200 leading-tight"
                  style={category.color ? { backgroundColor: `${category.color}20`, borderColor: `${category.color}40`, color: category.color } : {}}
                >
                  {typeof category === 'string' ? category : category.name}
                </span>
              ))}
              {report.categories.length > 2 && (
                <span className="text-xs text-urban-300 px-2 py-1 leading-tight">
                  +{report.categories.length - 2} more
                </span>
              )}
            </div>
          )}
          
          {/* Tags - displayed with original styling */}
          {report.tags && report.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {report.tags.slice(0, 2).map((tag, index) => (
                <span
                  key={`tag-${index}`}
                  className="px-2 py-1 text-xs bg-verge-100 text-verge-800 rounded-xl font-medium leading-tight"
                >
                  {tag}
                </span>
              ))}
              {report.tags.length > 2 && (
                <span className="text-xs text-urban-300 px-2 py-1 leading-tight">
                  +{report.tags.length - 2} more
                </span>
              )}
            </div>
          )}
        </div>
        
        {/* Meta Information - Mobile Optimized */}
        <div className="flex justify-between items-center text-xs sm:text-sm">
          <span className="text-urban-400 truncate flex-shrink">
            {formatDate(report.submittedAt)}
          </span>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            {/* Coins earned indicator */}
            {showCoins && (
              <GamificationGate feature="coins">
                <div className="flex items-center gap-1 text-cardboard font-medium">
                  <span className="text-treasure">🪙</span>
                  <span>+5</span>
                </div>
              </GamificationGate>
            )}
            
            {/* View count */}
            <div className="flex items-center gap-1 text-urban-400">
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              <span>{report.viewCount || 0}</span>
            </div>
          </div>
        </div>

        {/* Pickup date if available - Mobile Optimized */}
        {report.pickupDate && (
          <div className="mt-2 pt-2 border-t border-urban-100">
            <div className="text-xs text-urban-400 flex items-center gap-1">
              <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              <span className="truncate">Pickup: {formatDate(report.pickupDate)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TreasureCard;